import { type UseQueryResult, queryOptions, useQueries } from "@tanstack/react-query";

import type {
    AdIntelligenceEnumsPlacementType,
    AdIntelligenceEnumsScreenshotFilter,
    AdIntelligenceEnumsVerificationDevice,
    AdIntelligencePublisherVerificationRequest,
    AdIntelligencePublisherVerificationTotalsRequest,
    AdIntelligenceRecordsPublisherVerificationTotalsResponse,
    AdIntelligenceRecordsPublisherVerificationsResponse,
} from "@similarweb/pro-api-client";

import { apiClient } from "app/modules/api-client/apiClient";
import DurationService from "app/services/DurationService";

import { useDisplayAdvertiserPublisherDataAndParams } from "../../hooks/useDisplayAdvertiserPublisherDataAndParams";

interface IUseVerificationArgs {
    advertiser: string;
    campaignName?: string[] | null;
    publishers?: string[];
    device?: AdIntelligenceEnumsVerificationDevice;
    position?: AdIntelligenceEnumsPlacementType[] | null;
    page: number;
    pageSize: number;
    screenshotFilter?: AdIntelligenceEnumsScreenshotFilter;
}

export const useVerification = ({
    advertiser,
    campaignName,
    publishers,
    device = "All",
    position,
    page,
    pageSize,
    screenshotFilter = "All",
}: IUseVerificationArgs) => {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const durationData = DurationService.getDurationData(params.duration ?? "");
    const { from, to } = durationData.forAPI.iso;
    const totalDays = Math.max(durationData.raw.to.diff(durationData.raw.from, "day") + 1, 1);

    const verificationsBody: AdIntelligencePublisherVerificationRequest = {
        advertiser,
        campaignName,
        country: params.country,
        dateFrom: from,
        dateTo: to,
        device,
        page,
        pageSize,
        position,
        publishers,
        screenshotFilter,
    };

    const totalsBody: AdIntelligencePublisherVerificationTotalsRequest = {
        advertiser,
        campaignName,
        country: params.country,
        dateFrom: from,
        dateTo: to,
        device,
        position,
        publishers,
        screenshotFilter,
    };

    const enabled = Boolean(advertiser && params.country && from && to);

    // Verifications (paginated) + totals (single, reflects the same filter set) — fetched in parallel.
    const results = useQueries({
        queries: [
            queryOptions<AdIntelligenceRecordsPublisherVerificationsResponse>({
                queryKey: [
                    "WebsiteDisplayAds",
                    "verifications",
                    advertiser,
                    params.country,
                    from,
                    to,
                    device,
                    position ?? null,
                    campaignName ?? null,
                    publishers,
                    page,
                    pageSize,
                    screenshotFilter,
                ],
                queryFn: () =>
                    apiClient.AdIntelligenceMediaPartners.adIntelligenceAdvertiserPublisherVerificationsCreate(
                        verificationsBody,
                    ),
                enabled,
            }),
            queryOptions<AdIntelligenceRecordsPublisherVerificationTotalsResponse>({
                queryKey: [
                    "WebsiteDisplayAds",
                    "verificationsTotals",
                    advertiser,
                    params.country,
                    from,
                    to,
                    device,
                    position ?? null,
                    campaignName ?? null,
                    publishers,
                    screenshotFilter,
                ],
                queryFn: () =>
                    apiClient.AdIntelligenceMediaPartners.adIntelligenceAdvertiserPublisherVerificationTotalsCreate(
                        totalsBody,
                    ),
                enabled,
            }),
        ],
    });

    const verifications = results[0] as UseQueryResult<AdIntelligenceRecordsPublisherVerificationsResponse>;
    const totals = results[1] as UseQueryResult<AdIntelligenceRecordsPublisherVerificationTotalsResponse>;

    // Stays `true` while any query is still in flight — flips to `false` only once every query has settled.
    const isLoading = results.some((r) => r.isFetching);
    const isError = results.some((r) => r.isError);

    return {
        data: verifications.data,
        totals: totals.data,
        isLoading,
        isError,
        totalDays,
        dateFrom: from,
        dateTo: to,
    };
};
